import React from "react"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"
import clsx from "clsx"
import { isMobileOnly } from "react-device-detect"

import { useAsideMenu, useAuth, useTheme } from "hooks"
import { LanguageSelector } from "components/Elements/LanguageSelector"
import { LogoutIcon } from "components/Elements/Icons"
import { OperatorSelector } from "./components/OperatorSelector"
import { MenuLinks } from "./components"
import Timer from "components/shared/Timer"
import { CONTACT_SUPPORT_ID } from "utils/constants/menuTypes"
import { SubMenuLink } from "./components/SubMenuLinks"
import { SelectOperatorIcon } from "./components/SelectOperatorIcon"
import { LegalDocuments } from "../LegalDocuments"

import { userPermissionList, entitySpecificPermissionList } from "utils/constants"
import "./AsideNav.scoped.scss"
import { ChevronLeftIcon } from "components/Elements/Icons/ChevronLeft"
import { ChevronRightIcon } from "components/Elements/Icons/ChevrinRight"
import { Markdown } from "components/Elements/Markdown"

const SUPPORT_TOKEN_DURATION = 60 * 60 * 1000 // in ms

export const AsideNav: React.FC = React.memo(() => {
	const { user, entity, isConnectedFromSupport, logout, logoutAsSupport, isAdmin } = useAuth()
	const { currentTheme } = useTheme()
	const {
		currentActiveAsideMenu,
		isArrowShowing,
		operatorsUser,
		setIsArrowShowing,
		isMenuCollapsed,
		setIsMenuCollapsed,
		isCurrentActiveAsideMenuAvailableForOperator,
		mainMenuCollection,
		setActiveAsideMenu,
	} = useAsideMenu()
	const { t } = useTranslation()

	const entityRole = entity?.entity_data?.entity_permission?.role
	const isMANAGEBIKES = entityRole === "MANAGEBIKES"
	const isMANAGEENTITIES = entityRole === "MANAGEENTITIES"

	const isPartnerHasOnlyOneOperator = operatorsUser?.length === 1

	const currentOperator = entity?.data_operators?.find(
		(element) => element?.operator === currentTheme?.id_api
	)

	React.useEffect(() => {
		// Lors du changement d'opérateur, on remet le menu principal par défaut si l'opérateur n'a pas accès au menu actif
		if (!isCurrentActiveAsideMenuAvailableForOperator)
			setActiveAsideMenu(mainMenuCollection[0].id)
	}, [currentOperator]) // eslint-disable-line

	const walletId = currentOperator?.entity_data?.account_id ?? false

	return (
		<>
			{!currentTheme ? (
				<div className={"u-sticky-view"}>
					<div className="c-main-content flex justify-center items-center">
						<div className="c-spinner"></div>
					</div>
				</div>
			) : (
				<div
					className={clsx(
						"c-aside-navigation relative",
						{
							"is-collapsed": isMenuCollapsed,
						},
						{ "c-aside-navigation--mobile": isMobileOnly }
					)}
					onMouseEnter={() => setIsArrowShowing(true)}
					onMouseLeave={() => setIsArrowShowing(false)}
				>
					{/* Arrow */}
					<div
						className={clsx("c-aside__arrow z-30", {
							"opacity-0": !isArrowShowing,
							"right-[190px]": isMobileOnly && !isMenuCollapsed,
						})}
					>
						<button
							onClick={() => setIsMenuCollapsed(!isMenuCollapsed)}
							className="c-aside__arrow__inner"
						>
							{isMenuCollapsed ? (
								<ChevronRightIcon color="var(--grey60)" />
							) : (
								<ChevronLeftIcon color="var(--grey60)" />
							)}
						</button>
					</div>

					{/* Main menu */}
					<div className={"c-aside c-aside-main-menu p-2 py-4"}>
						<SelectOperatorIcon />

						<main className="flex-1">
							<hr className={clsx("c-aside__divider")} />
							<MenuLinks />
						</main>

						<footer className={clsx("c-aside__menuFooter")}>
							<div className="mb-3">
								<LanguageSelector />
							</div>

							<hr
								className={clsx("c-aside__divider", {
									"c-aside__divider--mobile": isMobileOnly,
								})}
							/>

							<div className="mx-auto mt-6 mb-3">
								<button
									data-cy={
										isConnectedFromSupport ? "support-logout" : "partner-logout"
									}
									onClick={isConnectedFromSupport ? logoutAsSupport : logout}
								>
									<LogoutIcon size={28} />
								</button>
							</div>
						</footer>
					</div>

					{/* Main mennu - sublinks */}
					<div
						className={clsx("c-aside c-aside-secondary-menu", {
							"c-aside-secondary-menu--mobile px-0": isMobileOnly,
						})}
					>
						<header
							className={clsx("h-[210px] pt-4", {
								"flex flex-col justify-center": isAdmin && !isConnectedFromSupport,
							})}
						>
							<div className={clsx({ "px-2": isMobileOnly, "px-6": !isMobileOnly })}>
								{isMANAGEBIKES && !isPartnerHasOnlyOneOperator && (
									<OperatorSelector />
								)}
							</div>

							<div className={clsx("px-6", { "mt-2": isPartnerHasOnlyOneOperator })}>
								<div className={clsx({ "mb-7": !isAdmin })}>
									<NavLink className={"text-white"} to="/">
										{currentTheme
											? React.createElement(currentTheme.logo, {
													className: clsx("text-white", {
														"w-3/4": isMobileOnly,
													}),
													style: undefined,
													vertical: undefined,
													width: undefined,
													height: undefined,
												})
											: null}
									</NavLink>

									{entity &&
										(!isAdmin || (isAdmin && isConnectedFromSupport)) && (
											<div
												className={clsx("uppercase", {
													"flex flex-col": isMobileOnly,
													"my-3": !isMobileOnly,
												})}
											>
												<span className="font-bold mr-1">
													{t("store.declarator_number", {
														ns: "partner",
													})}{" "}
													:
												</span>
												{entity.entity_id}
											</div>
										)}

									{walletId && (
										<div
											className={clsx("uppercase", {
												"w-2/3 my-2": isMobileOnly,
												"my-3": !isMobileOnly,
											})}
										>
											<Markdown>
												{t("bikeFleet.accountIDWithID", {
													ns: "bike",
													accountID: walletId,
												})}
											</Markdown>
										</div>
									)}
								</div>
							</div>

							{isAdmin && !isConnectedFromSupport && (
								<div className="uppercase mx-1">{t("menu.back_office")}</div>
							)}
						</header>

						<SubMenuLink />

						<footer className="c-aside__SubNavfooter pt-5 px-6">
							{currentTheme?.id !== "ocode" &&
								currentActiveAsideMenu === CONTACT_SUPPORT_ID && (
									<>
										<hr
											className={clsx("c-aside__divider", {
												"c-aside__divider--mobile": isMobileOnly,
											})}
										/>
										<LegalDocuments />
									</>
								)}
							<div className="text-m px-2 my-3 text-left font-[Jost] ">
								{isConnectedFromSupport && (
									<p className="text-xs italic mb-2">
										{t("support.connected_as")}
									</p>
								)}

								{user?.description?.prenom && user?.description?.nom ? (
									<div className="mb-3 font-bold">
										{user.description.prenom} {user.description.nom}
									</div>
								) : (
									<div className="mb-3">{`${user?.mail}`}</div>
								)}

								<div className="mt-3">
									{t(
										`${
											(isMANAGEENTITIES
												? entitySpecificPermissionList
												: userPermissionList
											).find((p) => p.value === user?.permission)?.label
										}`
									)}
								</div>

								{isConnectedFromSupport && (
									<div className="mt-2">
										<span className="italic mr-1">
											{t("support.remaining_time")}
										</span>
										<Timer
											className={"inline-block"}
											style={{ width: "40px" }}
											timeToWait={SUPPORT_TOKEN_DURATION}
											saveTimerInStorage={true}
										/>
									</div>
								)}

								<div className="text-xs mt-2">
									Version: {process.env.REACT_APP_VERSION} -{" "}
									{process.env.REACT_APP_MODE === "development" && "TEST"}
								</div>
							</div>
						</footer>
					</div>
				</div>
			)}
		</>
	)
})
